import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { UserPlus, Users, ShieldCheck, User, UserX, UserCheck, RefreshCw, Trash2 } from "lucide-react";
import { Modal } from "../../../components/ui/Modal";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { UserPicker } from "../../../components/ui/UserPicker";
import {
  useBusinessMembers,
  useAddBusinessMember,
  useSetMemberStatus,
} from "../../../hooks/useBusinesses";
import { useDeleteUser } from "../../../hooks/useUsers";
import { ApiError } from "../../../lib/api";
import type { Business, BusinessMember } from "../../../types/index";
import type { SelectedUser } from "../../../components/ui/UserPicker";

const inviteSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  email: z.string().email("Invalid email"),
  username: z.string().min(1, "Required"),
  isBusinessAdmin: z.boolean().default(false),
});
type InviteValues = z.infer<typeof inviteSchema>;

const INPUT_CLASS =
  "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";
const ERROR_CLASS = "text-[#dc2626] text-xs mt-1";

type SubMode = "none" | "invite" | "assign";

function generateUsername(first: string, last: string): string {
  const clean = (s: string) =>
    s.trim().toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "");
  const f = clean(first);
  const l = clean(last);
  if (!f && !l) return "";
  if (!l) return f;
  if (!f) return l;
  return `${f}.${l}`;
}

interface BusinessMembersModalProps {
  business: Business;
  isOpen: boolean;
  onClose: () => void;
}

export function BusinessMembersModal({ business, isOpen, onClose }: BusinessMembersModalProps) {
  const [subMode, setSubMode] = useState<SubMode>("none");
  const [assignUser, setAssignUser] = useState<SelectedUser | null>(null);
  const [assignAdmin, setAssignAdmin] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  // Track whether username was manually edited so we stop auto-generating
  const [usernameAutoMode, setUsernameAutoMode] = useState(true);

  const { data, isLoading, error } = useBusinessMembers(business.businessId);
  const addMember = useAddBusinessMember(business.businessId);
  const setMemberStatus = useSetMemberStatus(business.businessId);

  const {
    register,
    handleSubmit,
    setError,
    reset: resetInvite,
    setValue,
    watch,
    formState: { errors: inviteErrors, isSubmitting: inviteSubmitting },
  } = useForm<InviteValues>({ resolver: zodResolver(inviteSchema) });

  const members = data?.items ?? [];

  const handleInviteSubmit = async (values: InviteValues) => {
    try {
      await addMember.mutateAsync({
        mode: "invite",
        username: values.username,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        isBusinessAdmin: values.isBusinessAdmin,
      });
      resetInvite();
      setUsernameAutoMode(true);
      setSubMode("none");
    } catch (err) {
      if (err instanceof ApiError && err.code === "USERNAME_EXISTS") {
        setError("username", { message: "That username is already taken. Try a different one." });
      } else {
        setError("root", { message: "Failed to invite member. Please try again." });
      }
    }
  };

  const handleAssignSubmit = async () => {
    if (!assignUser) {
      setAssignError("Please select a user.");
      return;
    }
    setAssignError(null);
    try {
      await addMember.mutateAsync({
        mode: "assign",
        userId: assignUser.userId,
        isBusinessAdmin: assignAdmin,
      });
      setAssignUser(null);
      setAssignAdmin(false);
      setSubMode("none");
    } catch {
      setAssignError("Failed to assign member. Please try again.");
    }
  };

  const handleToggleStatus = (member: BusinessMember) => {
    const newStatus = member.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setMemberStatus.mutate({ userId: member.userId, status: newStatus });
  };

  const cancelSub = () => {
    setSubMode("none");
    resetInvite();
    setUsernameAutoMode(true);
    setAssignUser(null);
    setAssignAdmin(false);
    setAssignError(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Members — ${business.name}`}
      size="lg"
    >
      <div className="space-y-5">
        {/* Action buttons */}
        {subMode === "none" && (
          <div className="flex gap-2">
            <button
              onClick={() => setSubMode("invite")}
              className="flex items-center gap-1.5 rounded-lg border border-[#2447d9] px-3 py-2 text-sm font-medium text-[#2447d9] hover:bg-[#eff2ff] transition-colors"
            >
              <UserPlus className="h-4 w-4" />
              Invite New User
            </button>
            <button
              onClick={() => setSubMode("assign")}
              className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
            >
              <Users className="h-4 w-4" />
              Add Existing User
            </button>
          </div>
        )}

        {/* Invite form */}
        {subMode === "invite" && (
          <form
            onSubmit={handleSubmit(handleInviteSubmit)}
            className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-4 space-y-3"
          >
            <p className="text-sm font-semibold text-[#0f172a]">Invite New User</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLASS}>First Name</label>
                <input
                  {...register("firstName", {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      if (usernameAutoMode) {
                        const last = watch("lastName") ?? "";
                        setValue("username", generateUsername(e.target.value, last), {
                          shouldValidate: false,
                        });
                      }
                    },
                  })}
                  className={INPUT_CLASS}
                  placeholder="Juan"
                  autoFocus
                />
                {inviteErrors.firstName && (
                  <p className={ERROR_CLASS}>{inviteErrors.firstName.message}</p>
                )}
              </div>
              <div>
                <label className={LABEL_CLASS}>Last Name</label>
                <input
                  {...register("lastName", {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      if (usernameAutoMode) {
                        const first = watch("firstName") ?? "";
                        setValue("username", generateUsername(first, e.target.value), {
                          shouldValidate: false,
                        });
                      }
                    },
                  })}
                  className={INPUT_CLASS}
                  placeholder="dela Cruz"
                />
                {inviteErrors.lastName && (
                  <p className={ERROR_CLASS}>{inviteErrors.lastName.message}</p>
                )}
              </div>
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Username
                {usernameAutoMode && (
                  <span className="ml-1.5 text-[#94a3b8] font-normal">(auto-generated)</span>
                )}
              </label>
              <div className="relative">
                <input
                  {...register("username", {
                    onChange: () => setUsernameAutoMode(false),
                  })}
                  className={INPUT_CLASS}
                  placeholder="juan.delacruz"
                />
                {!usernameAutoMode && (
                  <button
                    type="button"
                    onClick={() => {
                      const first = watch("firstName") ?? "";
                      const last = watch("lastName") ?? "";
                      setValue("username", generateUsername(first, last), {
                        shouldValidate: false,
                      });
                      setUsernameAutoMode(true);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[#94a3b8] hover:text-[#2447d9] transition-colors"
                    title="Reset to auto-generated"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {inviteErrors.username && (
                <p className={ERROR_CLASS}>{inviteErrors.username.message}</p>
              )}
            </div>
            <div>
              <label className={LABEL_CLASS}>Email Address</label>
              <input
                {...register("email")}
                type="email"
                className={INPUT_CLASS}
                placeholder="juan@company.com"
              />
              {inviteErrors.email && (
                <p className={ERROR_CLASS}>{inviteErrors.email.message}</p>
              )}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...register("isBusinessAdmin")}
                className="h-4 w-4 rounded border-[#e2e8f0] accent-[#2447d9]"
              />
              <span className="text-sm text-[#374151]">Grant Business Admin access</span>
            </label>
            {inviteErrors.root && (
              <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">
                {inviteErrors.root.message}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={cancelSub}
                disabled={inviteSubmitting}
                className="rounded-lg border border-[#e2e8f0] px-3 py-1.5 text-sm font-medium text-[#374151] hover:bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={inviteSubmitting}
                className="rounded-lg bg-[#2447d9] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60"
              >
                {inviteSubmitting ? "Inviting…" : "Send Invite"}
              </button>
            </div>
          </form>
        )}

        {/* Assign existing user */}
        {subMode === "assign" && (
          <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-4 space-y-3">
            <p className="text-sm font-semibold text-[#0f172a]">Add Existing User</p>
            <div>
              <label className={LABEL_CLASS}>Search Employee</label>
              <UserPicker
                value={assignUser}
                onChange={setAssignUser}
                placeholder="Search by name, username…"
              />
              {assignError && <p className={ERROR_CLASS}>{assignError}</p>}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={assignAdmin}
                onChange={(e) => setAssignAdmin(e.target.checked)}
                className="h-4 w-4 rounded border-[#e2e8f0] accent-[#2447d9]"
              />
              <span className="text-sm text-[#374151]">Grant Business Admin access</span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={cancelSub}
                disabled={addMember.isPending}
                className="rounded-lg border border-[#e2e8f0] px-3 py-1.5 text-sm font-medium text-[#374151] hover:bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssignSubmit}
                disabled={addMember.isPending || !assignUser}
                className="rounded-lg bg-[#2447d9] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60"
              >
                {addMember.isPending ? "Adding…" : "Add Member"}
              </button>
            </div>
          </div>
        )}

        {/* Members list */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <p className="text-sm text-[#dc2626] bg-[#fef2f2] rounded-lg px-3 py-2">
            Failed to load members.
          </p>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Users className="h-8 w-8 text-[#cbd5e1] mb-2" />
            <p className="text-sm text-[#64748b]">No members yet</p>
            <p className="text-xs text-[#94a3b8] mt-0.5">Invite or assign users to this business.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-[#e2e8f0] bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#f1f5f9] bg-[#f8fafc]">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">
                    Name
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">
                    Role
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <MemberRow
                    key={m.userId}
                    member={m}
                    toggling={setMemberStatus.isPending && setMemberStatus.variables?.userId === m.userId}
                    onToggleStatus={handleToggleStatus}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function MemberRow({
  member,
  toggling,
  onToggleStatus,
}: {
  member: BusinessMember;
  toggling: boolean;
  onToggleStatus: (m: BusinessMember) => void;
}) {
  const isActive = member.status === "ACTIVE";
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteUser = useDeleteUser();

  const handleDelete = async () => {
    try {
      await deleteUser.mutateAsync(member.userId);
    } catch {
      setConfirmDelete(false);
    }
  };

  return (
    <tr
      className={`border-b border-[#f1f5f9] last:border-0 transition-colors ${
        isActive ? "hover:bg-[#f8fafc]" : "bg-[#fafafa] opacity-75"
      }`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#eff2ff]">
            <User className="h-3.5 w-3.5 text-[#2447d9]" />
          </div>
          <div>
            <p className="font-medium text-[#0f172a]">
              {member.firstName} {member.lastName}
            </p>
            <p className="text-xs text-[#64748b]">{member.username}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {member.isBusinessAdmin ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#eff2ff] px-2 py-0.5 text-xs font-medium text-[#2447d9]">
            <ShieldCheck className="h-3 w-3" />
            Admin
          </span>
        ) : (
          <span className="inline-block rounded-full bg-[#f1f5f9] px-2 py-0.5 text-xs text-[#64748b]">
            Member
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
            isActive
              ? "bg-[#dcfce7] text-[#16a34a]"
              : "bg-[#f1f5f9] text-[#64748b]"
          }`}
        >
          {member.status}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        {confirmDelete ? (
          <div className="flex flex-col items-end gap-1.5">
            <span className="text-xs text-[#dc2626] font-medium">Delete permanently?</span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleDelete}
                disabled={deleteUser.isPending}
                className="rounded-md bg-[#dc2626] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#b91c1c] disabled:opacity-60 transition-colors"
              >
                {deleteUser.isPending ? "Deleting…" : "Yes, delete"}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleteUser.isPending}
                className="rounded-md border border-[#e2e8f0] px-2.5 py-1 text-xs font-medium text-[#64748b] hover:bg-[#f1f5f9] disabled:opacity-60 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1">
            <button
              onClick={() => onToggleStatus(member)}
              disabled={toggling}
              className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:opacity-40 ${
                isActive
                  ? "text-[#94a3b8] hover:bg-[#fef2f2] hover:text-[#dc2626]"
                  : "text-[#94a3b8] hover:bg-[#dcfce7] hover:text-[#16a34a]"
              }`}
              aria-label={isActive ? `Deactivate ${member.firstName}` : `Reactivate ${member.firstName}`}
              title={isActive ? "Deactivate member" : "Reactivate member"}
            >
              {isActive ? <UserX className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#94a3b8] hover:bg-[#fef2f2] hover:text-[#dc2626] transition-colors"
              aria-label={`Delete ${member.firstName} ${member.lastName}`}
              title="Permanently delete user"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
